interface HotelSearchParams {
  destination: string;
  checkIn: string;
  checkOut: string;
  adults: number;
  rooms: number;
  currency: string;
}

interface HotelOffer {
  id: string;
  name: string;
  rating: number;
  address: string;
  city: string;
  country: string;
  price: {
    amount: number;
    currency: string;
  };
  amenities: string[];
  images: string[];
  description: string;
  chainCode?: string;
  brand?: string;
  roomType?: string;
  cancellation?: string;
  pointsRequired?: number;
  loyaltyProgram?: string;
}

interface HotelSearchResponse {
  hotels: HotelOffer[];
  total: number;
  currency: string;
}

interface HotelLocation {
  code: string;
  name: string;
  city: string;
  country: string;
  type: 'city' | 'airport' | 'landmark';
}

class HotelAPIService {
  // Use existing Amadeus API credentials
  private static readonly AMADEUS_API_KEY = process.env.REACT_APP_AMADEUS_API_KEY;
  private static readonly AMADEUS_SECRET = process.env.REACT_APP_AMADEUS_SECRET;
  private static readonly AMADEUS_BASE_URL = 'https://test.api.amadeus.com/v1';
  
  // Cache for access tokens
  private static accessToken: string | null = null;
  private static tokenExpiry: number = 0;

  // Popular hotel destinations
  private static readonly HOTEL_DESTINATIONS: HotelLocation[] = [
    { code: 'NYC', name: 'New York', city: 'New York', country: 'United States', type: 'city' },
    { code: 'LON', name: 'London', city: 'London', country: 'United Kingdom', type: 'city' },
    { code: 'PAR', name: 'Paris', city: 'Paris', country: 'France', type: 'city' },
    { code: 'TYO', name: 'Tokyo', city: 'Tokyo', country: 'Japan', type: 'city' },
    { code: 'DXB', name: 'Dubai', city: 'Dubai', country: 'UAE', type: 'city' },
    { code: 'SIN', name: 'Singapore', city: 'Singapore', country: 'Singapore', type: 'city' },
    { code: 'HKG', name: 'Hong Kong', city: 'Hong Kong', country: 'Hong Kong', type: 'city' },
    { code: 'SYD', name: 'Sydney', city: 'Sydney', country: 'Australia', type: 'city' },
    { code: 'BOM', name: 'Mumbai', city: 'Mumbai', country: 'India', type: 'city' },
    { code: 'DEL', name: 'New Delhi', city: 'New Delhi', country: 'India', type: 'city' },
    { code: 'BLR', name: 'Bangalore', city: 'Bangalore', country: 'India', type: 'city' },
    { code: 'LAX', name: 'Los Angeles', city: 'Los Angeles', country: 'United States', type: 'city' },
  ];

  // Major hotel chains and their loyalty programs
  private static readonly HOTEL_CHAINS = {
    'MC': { name: 'Marriott', loyalty: 'Marriott Bonvoy' },
    'HI': { name: 'Hilton', loyalty: 'Hilton Honors' },
    'HY': { name: 'Hyatt', loyalty: 'World of Hyatt' },
    'IC': { name: 'IHG', loyalty: 'IHG One Rewards' },
    'AC': { name: 'Accor', loyalty: 'ALL - Accor Live Limitless' },
    'RH': { name: 'Radisson', loyalty: 'Radisson Rewards' },
    'CH': { name: 'Choice', loyalty: 'Choice Privileges' },
    'WY': { name: 'Wyndham', loyalty: 'Wyndham Rewards' },
  };

  /**
   * Get popular hotel destinations
   */
  static getHotelDestinations(): HotelLocation[] {
    return this.HOTEL_DESTINATIONS;
  }

  /**
   * Main hotel search method - uses Amadeus API with fallback to enhanced static data
   */
  static async searchHotels(params: HotelSearchParams): Promise<HotelSearchResponse> {
    try {
      console.log('🏨 Searching hotels with params:', params);

      // Try Amadeus API first
      if (this.AMADEUS_API_KEY && this.AMADEUS_SECRET) {
        try {
          const amadeusResult = await this.searchHotelsAmadeus(params);
          if (amadeusResult.hotels.length > 0) {
            console.log('✅ Using Amadeus hotel data');
            return amadeusResult;
          }
        } catch (error) {
          console.warn('⚠️ Amadeus hotel API not available:', error);
        }
      } else {
        console.warn('⚠️ Amadeus API credentials not configured');
      }

      // Final fallback to enhanced static data
      console.log('📋 Using enhanced static hotel data');
      return this.generateEnhancedStaticData(params);

    } catch (error) {
      console.error('❌ Hotel search failed:', error);
      return this.generateEnhancedStaticData(params);
    }
  }

  /**
   * Get Amadeus access token
   */
  private static async getAmadeusAccessToken(): Promise<string> {
    // Check if we have a valid cached token
    if (this.accessToken && Date.now() < this.tokenExpiry) {
      return this.accessToken;
    }

    try {
      const response = await fetch(`${this.AMADEUS_BASE_URL}/security/oauth2/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: `grant_type=client_credentials&client_id=${this.AMADEUS_API_KEY}&client_secret=${this.AMADEUS_SECRET}`
      });

      const data = await response.json();
      
      if (!data.access_token) {
        throw new Error(`Token request failed: ${data.error_description || 'Unknown error'}`);
      }

      // Cache the token (expires in ~30 minutes)
      this.accessToken = data.access_token;
      this.tokenExpiry = Date.now() + (data.expires_in - 60) * 1000; // Refresh 1 min early

      console.log('✅ Amadeus access token acquired');
      return this.accessToken!; // We know it's not null here
      
    } catch (error) {
      console.error('❌ Failed to get Amadeus access token:', error);
      throw error;
    }
  }

  /**
   * Search hotels using Amadeus API
   */
  private static async searchHotelsAmadeus(params: HotelSearchParams): Promise<HotelSearchResponse> {
    try {
      const accessToken = await this.getAmadeusAccessToken();
      const cityCode = this.getCityCodeForDestination(params.destination);
      
      console.log(`🏨 Searching hotels in ${params.destination} (${cityCode}) via Amadeus`);
      
      // Search hotels by city
      const hotelsResponse = await fetch(
        `${this.AMADEUS_BASE_URL}/reference-data/locations/hotels/by-city?cityCode=${cityCode}&radius=50&radiusUnit=KM&hotelSource=ALL`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        }
      );

      if (!hotelsResponse.ok) {
        throw new Error(`Amadeus API error: ${hotelsResponse.status} ${hotelsResponse.statusText}`);
      }

      const hotelsData = await hotelsResponse.json();
      
      if (!hotelsData.data || hotelsData.data.length === 0) {
        console.log('⚠️ No hotels found via Amadeus API');
        throw new Error('No hotels found');
      }

      console.log(`✅ Found ${hotelsData.data.length} hotels via Amadeus`);
      
      // Convert Amadeus format to our format
      const hotels: HotelOffer[] = hotelsData.data.slice(0, 20).map((hotel: any, index: number) => ({
        id: hotel.hotelId || `amadeus-${index}`,
        name: hotel.name || 'Hotel Name',
        rating: this.generateRandomRating(),
        address: hotel.address?.lines?.join(', ') || 'Address not available',
        city: params.destination.split(',')[0],
        country: hotel.address?.countryCode || 'Country',
        price: {
          amount: this.generateDynamicPrice(params.destination, params.checkIn),
          currency: params.currency
        },
        amenities: this.generateRandomAmenities(),
        images: this.generateHotelImages(hotel.name),
        description: `${hotel.name} offers comfortable accommodation in ${params.destination}`,
        chainCode: hotel.chainCode,
        brand: this.getHotelBrand(hotel.chainCode),
        roomType: this.generateRoomType(),
        cancellation: 'Free cancellation until 24 hours before check-in',
        pointsRequired: this.calculatePointsRequired(this.generateDynamicPrice(params.destination, params.checkIn)),
        loyaltyProgram: this.getLoyaltyProgram(hotel.chainCode)
      }));

      return {
        hotels,
        total: hotels.length,
        currency: params.currency
      };

    } catch (error) {
      console.error('❌ Amadeus hotel search failed:', error);
      throw error;
    }
  }

  /**
   * Get city code for destination
   */
  private static getCityCodeForDestination(destination: string): string {
    const dest = destination.toLowerCase();
    
    // Popular destinations mapping
    const cityMappings: Record<string, string> = {
      'mumbai': 'BOM',
      'delhi': 'DEL', 
      'bangalore': 'BLR',
      'chennai': 'MAA',
      'kolkata': 'CCU',
      'hyderabad': 'HYD',
      'pune': 'PNQ',
      'new york': 'NYC',
      'london': 'LON',
      'paris': 'PAR',
      'tokyo': 'TYO',
      'dubai': 'DXB',
      'singapore': 'SIN',
      'hong kong': 'HKG',
      'sydney': 'SYD',
      'los angeles': 'LAX',
      'san francisco': 'SFO',
      'chicago': 'CHI',
      'miami': 'MIA',
      'las vegas': 'LAS',
      'barcelona': 'BCN',
      'rome': 'ROM',
      'amsterdam': 'AMS',
      'bangkok': 'BKK',
      'istanbul': 'IST'
    };

    for (const [city, code] of Object.entries(cityMappings)) {
      if (dest.includes(city)) {
        return code;
      }
    }

    // Default fallback
    return 'BOM'; // Mumbai as default
  }

  /**
   * Generate enhanced static hotel data as fallback
   */
  private static generateEnhancedStaticData(params: HotelSearchParams): HotelSearchResponse {
    const destinationHotels = this.getStaticHotelsForDestination(params.destination);
    
    const hotels: HotelOffer[] = destinationHotels.map((hotel, index) => ({
      ...hotel,
      id: `static-${index}`,
      price: {
        amount: this.generateDynamicPrice(params.destination, params.checkIn),
        currency: params.currency
      },
      pointsRequired: this.calculatePointsRequired(this.generateDynamicPrice(params.destination, params.checkIn))
    }));

    return {
      hotels,
      total: hotels.length,
      currency: params.currency
    };
  }

  /**
   * Get static hotel data for specific destinations
   */
  private static getStaticHotelsForDestination(destination: string): Omit<HotelOffer, 'id' | 'price' | 'pointsRequired'>[] {
    const dest = destination.toLowerCase();

    if (dest.includes('mumbai')) {
      return [
        {
          name: 'The Taj Mahal Palace',
          rating: 4.8,
          address: 'Apollo Bunder, Colaba, Mumbai',
          city: 'Mumbai',
          country: 'India',
          amenities: ['Free WiFi', 'Swimming Pool', 'Spa', 'Restaurant', 'Concierge', 'Business Center'],
          images: [
            'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800',
            'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=800',
            'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800',
            'https://images.unsplash.com/photo-1584132967334-10e028bd69f7?w=800'
          ],
          description: 'Iconic luxury hotel overlooking the Gateway of India',
          chainCode: 'TJ',
          brand: 'Taj Hotels',
          roomType: 'Deluxe Room',
          cancellation: 'Free cancellation until 24 hours before check-in',
          loyaltyProgram: 'Taj InnerCircle'
        },
        {
          name: 'JW Marriott Mumbai Sahar',
          rating: 4.6,
          address: 'IA Project Road, Chhatrapati Shivaji International Airport',
          city: 'Mumbai',
          country: 'India',
          amenities: ['Free WiFi', 'Swimming Pool', 'Fitness Center', 'Restaurant', 'Airport Shuttle'],
          images: [
            'https://images.unsplash.com/photo-1611892440504-42a792e24d32?w=800',
            'https://images.unsplash.com/photo-1590490360182-c33d57733427?w=800',
            'https://images.unsplash.com/photo-1618773928121-c32242e63f39?w=800',
            'https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=800'
          ],
          description: 'Luxury hotel near Mumbai International Airport',
          chainCode: 'MC',
          brand: 'Marriott',
          roomType: 'Superior Room',
          cancellation: 'Free cancellation until 48 hours before check-in',
          loyaltyProgram: 'Marriott Bonvoy'
        },
        {
          name: 'The St. Regis Mumbai',
          rating: 4.7,
          address: 'Senapati Bapat Marg, Lower Parel, Mumbai',
          city: 'Mumbai',
          country: 'India',
          amenities: ['Free WiFi', 'Swimming Pool', 'Spa', 'Restaurant', 'Butler Service', 'Business Center'],
          images: [
            'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800',
            'https://images.unsplash.com/photo-1590490360182-c33d57733427?w=800',
            'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800',
            'https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=800'
          ],
          description: 'Ultra-luxury hotel with exceptional service',
          chainCode: 'MC',
          brand: 'Marriott',
          roomType: 'Grand Deluxe Room',
          cancellation: 'Free cancellation until 48 hours before check-in',
          loyaltyProgram: 'Marriott Bonvoy'
        }
      ];
    } else if (dest.includes('delhi')) {
      return [
        {
          name: 'The Imperial New Delhi',
          rating: 4.7,
          address: 'Janpath, Connaught Place, New Delhi',
          city: 'New Delhi',
          country: 'India',
          amenities: ['Free WiFi', 'Swimming Pool', 'Spa', 'Restaurant', 'Business Center'],
          images: [
            'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=800',
            'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800',
            'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800',
            'https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?w=800'
          ],
          description: 'Historic luxury hotel in the heart of New Delhi',
          chainCode: 'IH',
          brand: 'Imperial Hotels',
          roomType: 'Heritage Room',
          cancellation: 'Free cancellation until 24 hours before check-in',
          loyaltyProgram: 'Imperial Rewards'
        },
        {
          name: 'The Leela Palace New Delhi',
          rating: 4.8,
          address: 'Diplomatic Enclave, Chanakyapuri, New Delhi',
          city: 'New Delhi',
          country: 'India',
          amenities: ['Free WiFi', 'Swimming Pool', 'Spa', 'Restaurant', 'Concierge', 'Business Center'],
          images: [
            'https://images.unsplash.com/photo-1549294413-26f195200c16?w=800',
            'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=800',
            'https://images.unsplash.com/photo-1596394516093-501ba68a0ba6?w=800',
            'https://images.unsplash.com/photo-1584132967334-10e028bd69f7?w=800'
          ],
          description: 'Palatial luxury hotel with royal architecture',
          brand: 'The Leela',
          roomType: 'Royal Club Room',
          cancellation: 'Free cancellation until 48 hours before check-in',
          loyaltyProgram: 'The Leela Privilege'
        }
      ];
    } else {
      return [
        {
          name: 'Grand Hotel ' + destination,
          rating: 4.2,
          address: 'City Center, ' + destination,
          city: destination,
          country: 'Country',
          amenities: ['Free WiFi', 'Restaurant', 'Fitness Center', 'Room Service'],
          images: [
            'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800',
            'https://images.unsplash.com/photo-1611892440504-42a792e24d32?w=800',
            'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800',
            'https://images.unsplash.com/photo-1590490360182-c33d57733427?w=800'
          ],
          description: `Comfortable accommodation in ${destination}`,
          brand: 'Independent',
          roomType: 'Standard Room',
          cancellation: 'Free cancellation until 24 hours before check-in',
          loyaltyProgram: ''
        },
        {
          name: 'Luxury Suites ' + destination,
          rating: 4.0,
          address: 'Business District, ' + destination,
          city: destination,
          country: 'Country',
          amenities: ['Free WiFi', 'Swimming Pool', 'Fitness Center', 'Business Center'],
          images: [
            'https://images.unsplash.com/photo-1596394516093-501ba68a0ba6?w=800',
            'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800',
            'https://images.unsplash.com/photo-1584132967334-10e028bd69f7?w=800',
            'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800'
          ],
          description: `Modern business hotel in ${destination}`,
          brand: 'Independent',
          roomType: 'Executive Suite',
          cancellation: 'Free cancellation until 24 hours before check-in',
          loyaltyProgram: ''
        }
      ];
    }
  }

  /**
   * Generate random rating for hotels
   */
  private static generateRandomRating(): number {
    return Math.round((3.5 + Math.random() * 1.5) * 10) / 10; // 3.5 to 5.0 stars
  }

  /**
   * Generate dynamic pricing based on destination and date
   */
  private static generateDynamicPrice(destination: string, checkIn: string): number {
    const basePrice = 120;
    
    // Destination multipliers
    const destinationMultiplier = destination.toLowerCase().includes('mumbai') ? 1.3 :
                                 destination.toLowerCase().includes('delhi') ? 1.2 :
                                 destination.toLowerCase().includes('bangalore') ? 1.1 :
                                 destination.toLowerCase().includes('new york') ? 2.8 :
                                 destination.toLowerCase().includes('london') ? 2.5 :
                                 destination.toLowerCase().includes('paris') ? 2.3 :
                                 destination.toLowerCase().includes('dubai') ? 1.9 :
                                 destination.toLowerCase().includes('singapore') ? 1.8 : 1.0;
    
    // Date-based pricing (weekends more expensive)
    const checkInDate = new Date(checkIn);
    const dayOfWeek = checkInDate.getDay();
    const weekendMultiplier = (dayOfWeek === 5 || dayOfWeek === 6) ? 1.2 : 1.0; // Friday/Saturday more expensive
    
    // Add some randomness
    const randomFactor = 0.8 + Math.random() * 0.4; // 0.8 to 1.2
    
    return Math.round(basePrice * destinationMultiplier * weekendMultiplier * randomFactor);
  }

  /**
   * Calculate points required for hotel stay
   */
  private static calculatePointsRequired(cashPrice: number): number {
    // Typical hotel loyalty conversion: $1 = 100-150 points
    const pointsPerDollar = 120 + Math.random() * 30; // 120-150 points per dollar
    return Math.round(cashPrice * pointsPerDollar);
  }

  /**
   * Generate random amenities
   */
  private static generateRandomAmenities(): string[] {
    const allAmenities = [
      'Free WiFi', 'Swimming Pool', 'Fitness Center', 'Spa', 'Restaurant',
      'Room Service', 'Concierge', 'Business Center', 'Parking', 'Airport Shuttle',
      'Bar/Lounge', 'Pet Friendly', 'Air Conditioning', 'Laundry Service',
      'Meeting Rooms', 'Babysitting', 'Currency Exchange', 'Tour Desk'
    ];
    
    const count = Math.floor(Math.random() * 6) + 4; // 4-9 amenities
    const shuffled = allAmenities.sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  }

  /**
   * Generate hotel images using free image APIs
   * Uses Pexels API (free, no rate limits) and hotel-specific fallbacks
   */
  private static generateHotelImages(hotelName: string): string[] {
    const lowerName = hotelName.toLowerCase();
    
    // Check for known hotel chains with specific images
    const hotelChainImages: Record<string, string[]> = {
      'taj': [
        'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=800',
        'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800',
        'https://images.unsplash.com/photo-1596394516093-501ba68a0ba6?w=800',
        'https://images.unsplash.com/photo-1549294413-26f195200c16?w=800'
      ],
      'marriott': [
        'https://images.unsplash.com/photo-1611892440504-42a792e24d32?w=800',
        'https://images.unsplash.com/photo-1590490360182-c33d57733427?w=800',
        'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=800',
        'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800'
      ],
      'hilton': [
        'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800',
        'https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=800',
        'https://images.unsplash.com/photo-1587985064135-0366536eab42?w=800',
        'https://images.unsplash.com/photo-1512918728675-ed5a9ecdebfd?w=800'
      ],
      'hyatt': [
        'https://images.unsplash.com/photo-1455587734955-081b22074882?w=800',
        'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=800',
        'https://images.unsplash.com/photo-1566665797739-1674de7a421a?w=800',
        'https://images.unsplash.com/photo-1561501900-3701fa6a0864?w=800'
      ],
      'imperial': [
        'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=800',
        'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800',
        'https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?w=800',
        'https://images.unsplash.com/photo-1584132967334-10e028bd69f7?w=800'
      ],
      'leela': [
        'https://images.unsplash.com/photo-1549294413-26f195200c16?w=800',
        'https://images.unsplash.com/photo-1596394516093-501ba68a0ba6?w=800',
        'https://images.unsplash.com/photo-1540541338287-41700207dee6?w=800',
        'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800'
      ],
      'regis': [
        'https://images.unsplash.com/photo-1590490360182-c33d57733427?w=800',
        'https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=800',
        'https://images.unsplash.com/photo-1625244724120-1fd1d34d00f6?w=800',
        'https://images.unsplash.com/photo-1568084680786-a84f91d1153c?w=800'
      ],
      'sheraton': [
        'https://images.unsplash.com/photo-1444201983204-c43cbd584d93?w=800',
        'https://images.unsplash.com/photo-1566195992011-5f6b21e539ce?w=800',
        'https://images.unsplash.com/photo-1559508551-44bff1de756b?w=800',
        'https://images.unsplash.com/photo-1584132915807-8ebeb6ef4b7d?w=800'
      ],
      'radisson': [
        'https://images.unsplash.com/photo-1535827841776-24afc1e255ac?w=800',
        'https://images.unsplash.com/photo-1611048267451-e6ed903d4a38?w=800',
        'https://images.unsplash.com/photo-1563911302283-d2bc129e7570?w=800',
        'https://images.unsplash.com/photo-1586611292717-f828b167408c?w=800'
      ],
      'intercontinental': [
        'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=800',
        'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800',
        'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=800',
        'https://images.unsplash.com/photo-1540541338287-41700207dee6?w=800'
      ],
      'ihg': [
        'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=800',
        'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800',
        'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=800',
        'https://images.unsplash.com/photo-1540541338287-41700207dee6?w=800'
      ],
      'westin': [
        'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=800',
        'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800',
        'https://images.unsplash.com/photo-1596178060671-7a80dc8059ea?w=800',
        'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800'
      ],
      'oberoi': [
        'https://images.unsplash.com/photo-1549294413-26f195200c16?w=800',
        'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=800',
        'https://images.unsplash.com/photo-1596394516093-501ba68a0ba6?w=800',
        'https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?w=800'
      ],
      'itc': [
        'https://images.unsplash.com/photo-1549294413-26f195200c16?w=800',
        'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800',
        'https://images.unsplash.com/photo-1584132967334-10e028bd69f7?w=800',
        'https://images.unsplash.com/photo-1596394516093-501ba68a0ba6?w=800'
      ],
      'ritz': [
        'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800',
        'https://images.unsplash.com/photo-1590490360182-c33d57733427?w=800',
        'https://images.unsplash.com/photo-1625244724120-1fd1d34d00f6?w=800',
        'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800'
      ],
      'four seasons': [
        'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800',
        'https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=800',
        'https://images.unsplash.com/photo-1596178060671-7a80dc8059ea?w=800',
        'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=800'
      ],
      'peninsula': [
        'https://images.unsplash.com/photo-1455587734955-081b22074882?w=800',
        'https://images.unsplash.com/photo-1566665797739-1674de7a421a?w=800',
        'https://images.unsplash.com/photo-1561501900-3701fa6a0864?w=800',
        'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800'
      ],
      'crowne plaza': [
        'https://images.unsplash.com/photo-1611048267451-e6ed903d4a38?w=800',
        'https://images.unsplash.com/photo-1563911302283-d2bc129e7570?w=800',
        'https://images.unsplash.com/photo-1586611292717-f828b167408c?w=800',
        'https://images.unsplash.com/photo-1584132915807-8ebeb6ef4b7d?w=800'
      ],
      'holiday inn': [
        'https://images.unsplash.com/photo-1444201983204-c43cbd584d93?w=800',
        'https://images.unsplash.com/photo-1566195992011-5f6b21e539ce?w=800',
        'https://images.unsplash.com/photo-1559508551-44bff1de756b?w=800',
        'https://images.unsplash.com/photo-1535827841776-24afc1e255ac?w=800'
      ]
    };

    // Check if this hotel matches any known chain
    for (const [chain, images] of Object.entries(hotelChainImages)) {
      if (lowerName.includes(chain)) {
        return images;
      }
    }

    // Generate varied fallback images based on hotel name hash
    // This ensures each hotel gets consistent but different images
    const allHotelImages = [
      'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800',
      'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800',
      'https://images.unsplash.com/photo-1618773928121-c32242e63f39?w=800',
      'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800',
      'https://images.unsplash.com/photo-1611892440504-42a792e24d32?w=800',
      'https://images.unsplash.com/photo-1590490360182-c33d57733427?w=800',
      'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=800',
      'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800',
      'https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=800',
      'https://images.unsplash.com/photo-1455587734955-081b22074882?w=800',
      'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=800',
      'https://images.unsplash.com/photo-1566665797739-1674de7a421a?w=800',
      'https://images.unsplash.com/photo-1444201983204-c43cbd584d93?w=800',
      'https://images.unsplash.com/photo-1566195992011-5f6b21e539ce?w=800',
      'https://images.unsplash.com/photo-1559508551-44bff1de756b?w=800',
      'https://images.unsplash.com/photo-1535827841776-24afc1e255ac?w=800'
    ];

    // Simple hash function to get consistent variation based on hotel name
    const hash = hotelName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const startIndex = hash % (allHotelImages.length - 3);
    
    return [
      allHotelImages[startIndex],
      allHotelImages[startIndex + 1],
      allHotelImages[startIndex + 2],
      allHotelImages[(startIndex + 3) % allHotelImages.length]
    ];
  }

  /**
   * Generate room type
   */
  private static generateRoomType(): string {
    const roomTypes = [
      'Standard Room', 'Deluxe Room', 'Superior Room', 'Executive Room', 
      'Suite', 'Junior Suite', 'Premium Room', 'Club Room'
    ];
    return roomTypes[Math.floor(Math.random() * roomTypes.length)];
  }

  /**
   * Get hotel brand from chain code
   */
  private static getHotelBrand(chainCode?: string): string {
    if (!chainCode) return 'Independent';
    
    const chain = this.HOTEL_CHAINS[chainCode as keyof typeof this.HOTEL_CHAINS];
    return chain ? chain.name : 'Independent';
  }

  /**
   * Get loyalty program for chain code
   */
  private static getLoyaltyProgram(chainCode?: string): string {
    if (!chainCode) return '';
    
    const chain = this.HOTEL_CHAINS[chainCode as keyof typeof this.HOTEL_CHAINS];
    return chain ? chain.loyalty : '';
  }

  /**
   * Search hotel locations for autocomplete
   */
  static async searchHotelLocations(query: string): Promise<HotelLocation[]> {
    const normalizedQuery = query.toLowerCase().trim();
    
    if (normalizedQuery.length < 2) {
      return [];
    }

    // Filter destinations based on query
    const filtered = this.HOTEL_DESTINATIONS.filter(dest => 
      dest.name.toLowerCase().includes(normalizedQuery) ||
      dest.city.toLowerCase().includes(normalizedQuery) ||
      dest.country.toLowerCase().includes(normalizedQuery)
    );

    return filtered.slice(0, 10); // Return top 10 matches
  }
}

export default HotelAPIService;
export type { HotelSearchParams, HotelOffer, HotelSearchResponse, HotelLocation };